import {
  checkTier,
  EventRFeature,
  OrganisationUpdateDto,
  spError,
  TOrgConfig,
  USER_ADDITIONAL_INFO_SEVERITY,
} from "@eventr/common";
import { StateCreator } from "zustand";
import { wpApiClient } from "../data/apiClients";
import { EventrGatewayAPI } from "../data/gateway.api";
import i18n from "../i18n/i18next";
import { claimDerivedTabFlags } from "../utils";
import { BoundStore, bs, useBoundStore } from "./bound.store";
import { defaultAddSnack } from "./overlayState/overlayContext";

export interface OrgConfigSlice {
  orgConfig: TOrgConfig;
  setOrgConfig: (config: TOrgConfig) => void;
  fetchOrgConfig: () => Promise<void>;
  checkFeatureEnabled: (feature: EventRFeature) => boolean;
  /**
   *  Update organisation details.
   * @param orgInfo - Organisation info to update
   * @returns
   */
  /** Resolves true once the organisation is updated and its config refetched. */
  updateOrgDetails: (orgInfo: Omit<OrganisationUpdateDto, "_id">) => Promise<boolean>;
  /**
   * Sync subscription with the backend.
   * @returns
   */
  syncSubscription: (options?: { untilClaimsChange?: boolean }) => Promise<void>;
  getOrgUsersSeverityLevel: () => number;
  chatEnabled: boolean;
  teamPageEnabled: boolean;
  chatUnreadCount: number;
  setChatUnreadCount: (count: number) => void;
}

/** Delays between claim re-reads while claims settle. */
export const CLAIMS_SETTLE_BACKOFF_MS = [0, 500, 1000, 2000];

export const createOrgConfigSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], OrgConfigSlice> = (
  set,
  get,
) => ({
  orgConfig: {
    isWizardComplete: false,
    isPremium: false,
    isRocketChatInitialised: false,
    departmentTags: [],
    groupTags: [],
    orgNiceName: "",
    editInAppDisabled: false,
    userInviteDisabled: false,
    adminEditOnly: false,
    disableChat: false,
    disableLocationServices: false,
    enableBookingService: false,
    enableFlightBooking: false,
    enableExpensesTracking: false,
    themes: [],
    usersAdditionalInfoSeverity: USER_ADDITIONAL_INFO_SEVERITY.none,
  },
  // Two writes, not one: the flags read orgConfig back out of the store, so they have to
  // be derived after the new config has landed rather than as an argument alongside it.
  setOrgConfig: (config: TOrgConfig) => {
    set({ orgConfig: config }, false, "setOrgConfig");
    set(claimDerivedTabFlags(), false, "setOrgConfig/tabFlags");
  },
  fetchOrgConfig: async () => {
    const config = await wpApiClient.get<TOrgConfig>("itinerary/v1/config", { NOT_IN_GUEST: true });
    if (config.status === "success" && config.data) {
      // Through the action: swapping organisation fetches the new org's config after the
      // claims have already been re-read, so this is the only point at which `disableChat`
      // for the org being switched to is known.
      get().setOrgConfig(config.data);
    }
  },
  checkFeatureEnabled: (feature: EventRFeature) => {
    const { tier } = useBoundStore.getState();
    return tier ? checkTier(tier, feature) : false;
  },
  updateOrgDetails: async (orgInfo: Omit<OrganisationUpdateDto, "_id">) => {
    const orgId = bs().selectedOrg?.name;
    if (!orgId) {
      spError("Organisation ID is not set");
      return false;
    }
    try {
      const response = await EventrGatewayAPI.organisation.updateDetails({
        ...orgInfo,
        _id: orgId,
      });
      if (response.status !== "success") return false;
      await get().fetchOrgConfig();
      return true;
    } catch (error) {
      spError("Error updating organisation info", error);
      return false;
    }
  },
  syncSubscription: async ({ untilClaimsChange = false } = {}) => {
    const { selectedOrg } = get();
    if (!selectedOrg) return;

    // The tier arrives in the claims JWT on an API response header, once the purchase has
    // reached the backend by webhook.
    const claimsAtStart = useBoundStore.getState().claimsVersion;
    const claimsChanged = () => useBoundStore.getState().claimsVersion !== claimsAtStart;

    await EventrGatewayAPI.organisation.syncSubscription(selectedOrg.name);

    // Checks once unless untilClaimsChange, which retries on the backoff until the claims move.
    for (const delay of untilClaimsChange ? CLAIMS_SETTLE_BACKOFF_MS : [0]) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      await get().fetchOrgConfig();
      if (claimsChanged()) return;
    }
  },
  getOrgUsersSeverityLevel: () => {
    const severity = get().orgConfig.usersAdditionalInfoSeverity;
    return severity !== undefined ? severity : USER_ADDITIONAL_INFO_SEVERITY.none;
  },
  chatEnabled: false,
  teamPageEnabled: false,
  chatUnreadCount: 0,
  setChatUnreadCount: (count: number) => set({ chatUnreadCount: count }),
});

/**
 * Starts this module's store subscriptions. `startStoreSubscriptions` (state/store-subscriptions.ts)
 * calls it once, from the app entry, after the whole store graph has loaded.
 */
export async function startOrgConfigSubscriptions() {
  // Dynamic: guest.store reaches back into bound.store, so a static import here would be a cycle.
  const { useGuestStore } = await import("../feat/guest/guest.store");

  useBoundStore.subscribe(
    (state) => state.wpUser,
    () => {
      useBoundStore.setState(claimDerivedTabFlags(), false, "setTabFlags-wpUser");
    },
  );
  // Entering guest mode zeroes both flags; leaving re-derives them from the current claims.
  useGuestStore.subscribe(
    (state) => state.isGuestMode,
    (isGuestMode) => {
      if (isGuestMode) return;
      useBoundStore.setState(claimDerivedTabFlags(), false, "setTabFlags-guestExit");
    },
  );
  useBoundStore.subscribe(
    (state) => state.userClaims,
    () => {
      useBoundStore.setState(claimDerivedTabFlags(), false, "setTabFlags-claims");
    },
  );
  useBoundStore.subscribe(
    (state) => ({
      config: state.orgConfig,
      currentOrg: state.selectedOrg,
      wpuser: state.wpUser,
    }),
    (state) => {
      if (state.currentOrg?.roles?.includes("owner")) {
        if (state.config.multipleSubscription) {
          if (!localStorage.getItem("eventrProUnsubscribe")) {
            localStorage.setItem("eventrProUnsubscribe", "1");
            defaultAddSnack(i18n.t("snack.multipleSubscriptions"), "warning");
          }
        }
        if (state.wpuser?.claimsJWT) {
          const hasThingsToSend = localStorage.getItem(`sendOrgDetails-${state.currentOrg.friendlyName}`);
          if (hasThingsToSend) {
            localStorage.removeItem(`sendOrgDetails-${state.currentOrg.friendlyName}`);
            bs().updateOrgDetails(JSON.parse(hasThingsToSend));
          }
        }
      }
    },
    {
      fireImmediately: true,
    },
  );
}
